import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, useMediaQuery } from '@mui/material';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import EastRounded from '@mui/icons-material/EastRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

const FLOW = [
    { key: 'session', title: 'Session verified', Icon: TaskAltOutlined },
    { key: 'travel', title: 'Travel confirmed', Icon: RouteOutlined },
    { key: 'docs', title: 'Documentation complete', Icon: DescriptionOutlined },
    { key: 'payout', title: 'Payout approved', Icon: PaymentsOutlined },
];

const PHASE_MS = 2300;

const PayoutCalculationSection = () => {
    const isDesktop = useMediaQuery('(min-width:900px)');
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [phase, setPhase] = useState(0);

    useEffect(() => {
        if (reduceMotion) return undefined;
        const id = setInterval(() => {
            setPhase((prev) => (prev + 1) % 5);
        }, PHASE_MS);
        return () => clearInterval(id);
    }, [reduceMotion]);

    const current = reduceMotion ? 4 : phase;
    const complete = (i) => current === 4 || i < current;
    const active = (i) => !reduceMotion && i === current && current < 4;

    return (
        <Box
            component="section"
            aria-labelledby="payout-calculation-heading"
            sx={{
                pt: { xs: 7, md: 7.5, lg: 8 },
                pb: { xs: 5, md: 5.5, lg: 6 },
                background: 'linear-gradient(180deg, #F9FAFF 0%, #FFFFFF 100%)',
                borderBottom: '1px solid rgba(99, 102, 241, 0.10)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: 620,
                    height: 620,
                    borderRadius: '50%',
                    top: -260,
                    right: -220,
                    background: 'radial-gradient(circle, rgba(79, 70, 229, 0.10) 0%, transparent 72%)',
                    pointerEvents: 'none',
                },
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto', mb: { xs: 4, md: 5.5 } }}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: '#4338CA',
                            bgcolor: 'rgba(79, 70, 229, 0.10)',
                            px: { xs: 1.75, sm: 2.25 },
                            py: 0.75,
                            borderRadius: 999,
                            mb: 2.5,
                        }}
                    >
                        PAYOUT CALCULATION
                    </Typography>

                    <Typography
                        id="payout-calculation-heading"
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            letterSpacing: '-0.03em',
                            color: '#0F172A',
                            mb: 2.5,
                        }}
                    >
                        How payouts get calculated
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' },
                            lineHeight: 1.75,
                            color: 'rgba(15, 23, 42, 0.78)',
                        }}
                    >
                        Every disputed payment cycle traces back to the same gap - no verified record to refer to. meDZhub closes
                        that gap before the cycle opens. Nothing enters the payout queue that hasn&apos;t been verified by the system.
                        The sequence matters: session logged, travel confirmed, documentation complete. Nothing moves to the payout
                        queue until all three are done.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isDesktop ? 'row' : 'column',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        maxWidth: 1140,
                        mx: 'auto',
                        mb: { xs: 3, md: 4 },
                    }}
                >
                    {FLOW.map((item, index) => (
                        <React.Fragment key={item.key}>
                            <FlowCard
                                item={item}
                                index={index}
                                isActive={active(index)}
                                isComplete={complete(index)}
                                reduceMotion={reduceMotion}
                            />
                            {index < FLOW.length - 1 && (
                                <FlowConnector
                                    vertical={!isDesktop}
                                    isLit={current > index}
                                    isAnimating={active(index)}
                                    reduceMotion={reduceMotion}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </Box>

                <Typography
                    sx={{
                        textAlign: 'center',
                        fontSize: { xs: '1.02rem', md: '1.12rem' },
                        lineHeight: 1.7,
                        color: '#0F172A',
                        fontWeight: 500,
                        maxWidth: 860,
                        mx: 'auto',
                    }}
                >
                    There&apos;s nothing to audit, nothing to argue. The record is already there.
                </Typography>
            </Container>
        </Box>
    );
};

function FlowCard({ item, index, isActive, isComplete, reduceMotion }) {
    const { Icon, title } = item;

    return (
        <Box
            sx={{
                flex: { md: '1 1 0' },
                minWidth: 0,
                ...(!reduceMotion && {
                    animation: 'flowRiseIn 560ms ease-out backwards',
                    animationDelay: `${0.08 + index * 0.11}s`,
                    '@keyframes flowRiseIn': {
                        '0%': { opacity: 0, transform: 'translateY(18px) scale(0.98)' },
                        '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
                    },
                }),
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    minHeight: { xs: 112, md: 136 },
                    borderRadius: '20px',
                    px: { xs: 2, md: 2.2 },
                    py: { xs: 2.2, md: 2.6 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    background: isActive
                        ? 'linear-gradient(145deg, #FFFFFF 0%, #F2F0FF 58%, #ECEBFF 100%)'
                        : isComplete
                          ? 'linear-gradient(145deg, #FFFFFF 0%, #ECFDF5 58%, #F7FDFB 100%)'
                          : 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
                    border: isActive
                        ? '1.5px solid rgba(99, 102, 241, 0.42)'
                        : isComplete
                          ? '1.5px solid rgba(16, 185, 129, 0.35)'
                          : '1px solid rgba(15, 23, 42, 0.10)',
                    boxShadow: isActive
                        ? '0 18px 44px rgba(79, 70, 229, 0.20)'
                        : isComplete
                          ? '0 12px 30px rgba(16, 185, 129, 0.12)'
                          : '0 8px 22px rgba(15, 23, 42, 0.05)',
                    transition: 'all 360ms ease',
                    transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
                    ...(isActive &&
                        !reduceMotion && {
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: -1,
                                borderRadius: '20px',
                                border: '1px solid rgba(129, 140, 248, 0.55)',
                                animation: 'haloBreathe 1.6s ease-in-out infinite',
                            },
                            '@keyframes haloBreathe': {
                                '0%, 100%': { opacity: 0.35 },
                                '50%': { opacity: 0.85 },
                            },
                        }),
                }}
            >
                {isComplete && (
                    <CheckCircleRounded
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            fontSize: 22,
                            color: '#10B981',
                        }}
                    />
                )}

                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.2,
                        color: isActive ? '#4F46E5' : isComplete ? '#059669' : 'rgba(51, 65, 85, 0.72)',
                        background: isActive
                            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.18) 0%, rgba(139, 92, 246, 0.16) 100%)'
                            : isComplete
                              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.18) 0%, rgba(20, 184, 166, 0.12) 100%)'
                              : 'linear-gradient(135deg, rgba(15, 23, 42, 0.07) 0%, rgba(15, 23, 42, 0.04) 100%)',
                    }}
                >
                    <Icon sx={{ fontSize: 28 }} />
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: '0.96rem', md: '1rem' },
                        fontWeight: 700,
                        color: '#0F172A',
                        lineHeight: 1.38,
                    }}
                >
                    {title}
                </Typography>
            </Box>
        </Box>
    );
}

function FlowConnector({ vertical, isLit, isAnimating, reduceMotion }) {
    return (
        <Box
            sx={{
                flex: vertical ? 'none' : '0 0 44px',
                width: vertical ? 22 : 44,
                height: vertical ? 36 : 136,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                my: vertical ? 1 : 0,
                position: 'relative',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    ...(vertical
                        ? { width: 2, top: 0, bottom: 0 }
                        : { height: 2, left: 0, right: 0 }),
                    borderRadius: 999,
                    background: isLit
                        ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.55), rgba(167, 139, 250, 0.55))'
                        : 'rgba(148, 163, 184, 0.35)',
                }}
            />
            {isAnimating && !reduceMotion && (
                <Box
                    sx={{
                        position: 'absolute',
                        ...(vertical
                            ? { width: 2, top: 0, bottom: 0 }
                            : { height: 2, left: 0, right: 0 }),
                        borderRadius: 999,
                        background: vertical
                            ? 'linear-gradient(180deg, rgba(99, 102, 241, 0) 0%, rgba(99, 102, 241, 0.95) 45%, rgba(167, 139, 250, 0) 100%)'
                            : 'linear-gradient(90deg, rgba(99, 102, 241, 0) 0%, rgba(99, 102, 241, 0.95) 45%, rgba(167, 139, 250, 0) 100%)',
                        animation: vertical ? 'connectorSweepY 1.1s linear infinite' : 'connectorSweepX 1.1s linear infinite',
                        '@keyframes connectorSweepX': {
                            '0%': { clipPath: 'inset(0 100% 0 0)' },
                            '100%': { clipPath: 'inset(0 0 0 100%)' },
                        },
                        '@keyframes connectorSweepY': {
                            '0%': { clipPath: 'inset(0 0 100% 0)' },
                            '100%': { clipPath: 'inset(100% 0 0 0)' },
                        },
                    }}
                />
            )}
            <EastRounded
                sx={{
                    fontSize: vertical ? 18 : 20,
                    color: isLit ? 'rgba(79, 70, 229, 0.82)' : 'rgba(100, 116, 139, 0.55)',
                    transform: vertical ? 'rotate(90deg)' : 'none',
                    bgcolor: '#FFFFFF',
                    borderRadius: '50%',
                    p: '2px',
                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
                }}
            />
        </Box>
    );
}

export default PayoutCalculationSection;
